// Purchase Orders on the phone (Ryan, Oct 18 2026): the shop's "Write PO" tile became
// "Purchase Orders" — write a new one, or search the ones already written. Read-only:
// changing a PO stays with the office (the server refuses shop edits anyway).
//
// /shop/pos?po=<PO number or id> opens that PO straight away, so the bell, the
// assistant's "Take me there" and anyone's link land on the PO itself.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiFetch, getToken, API_BASE } from '../services/apiClient';
import { PO_STATUS_COLORS, PoShipBits } from './admin/PoDetailModal';

const fmt$ = n => (n == null || n === '' ? '—' : Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const day = iso => { if (!iso) return ''; const d = new Date(String(iso).length === 10 ? iso + 'T12:00:00' : iso); return isNaN(d) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); };
const who = po => `${po.first_name || ''} ${po.last_name || ''}`.trim();
const jobOf = po => [po.project_number, po.project_name && po.project_name !== po.project_number ? po.project_name.replace(new RegExp('^' + String(po.project_number || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\s*-?\\s*'), '') : ''].filter(Boolean).join(' — ');

// Find a PO by what someone would say: its number ("20229", "#20229", "PO 20229") or its id.
export function findPo(pos, key) {
  const k = String(key || '').trim();
  if (!k) return null;
  const num = k.replace(/^(po\s*)?#?\s*/i, '');
  return pos.find(p => String(p.id) === k) || pos.find(p => String(p.po_number) === num) || null;
}

function StatusChip({ status }) {
  const c = PO_STATUS_COLORS[status] || { bg: '#f3f4f6', text: '#374151' };
  return <span style={{ fontSize: 11.5, fontWeight: 800, padding: '3px 9px', borderRadius: 999, background: c.bg, color: c.text, whiteSpace: 'nowrap' }}>{status || 'Pending'}</span>;
}

function PoSheet({ po, onClose }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  async function viewPdf() {
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${API_BASE}/purchase-orders/${po.id}/pdf`, { headers: { 'x-worker-token': getToken() } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { setErr('Could not open the PO.'); }
    setBusy(false);
  }
  const row = (k, v) => (v ? (
    <div style={{ padding: '10px 0', borderBottom: '1px solid #f1f1f1' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4 }}>{k}</div>
      <div style={{ fontSize: 15, color: '#111827', marginTop: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v}</div>
    </div>
  ) : null);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 5000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', borderRadius: '18px 18px 0 0', padding: '16px 18px 24px', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ fontSize: 22, fontWeight: 900, color: '#111827' }}>PO #{po.po_number}</div>
          <StatusChip status={po.status} />
          <span style={{ flex: 1 }} />
          <button onClick={onClose} aria-label="Close" style={{ width: 36, height: 36, borderRadius: 999, border: 'none', background: '#f3f4f6', fontSize: 20, cursor: 'pointer' }}>×</button>
        </div>
        <div style={{ marginTop: 8 }}><PoShipBits po={po} /></div>
        {row('Job', jobOf(po))}
        {row('Vendor', po.vendor)}
        {row('What it is for', po.description)}
        {row('Notes', po.notes)}
        {row('Amount', po.cost != null && po.cost !== '' ? fmt$(po.cost) : '')}
        {row('Ship date', day(po.ship_date))}
        {row('Ship to', po.ship_to)}
        {row('Equipment', [po.equipment_asset, po.equipment_name].filter(Boolean).join(' · '))}
        {row('Written', [day(po.created_at), who(po)].filter(Boolean).join(' · by '))}
        {err && <div style={{ color: '#b91c1c', fontWeight: 700, marginTop: 10 }}>{err}</div>}
        <button onClick={viewPdf} disabled={busy}
          style={{ marginTop: 16, width: '100%', padding: '14px', borderRadius: 12, border: '1px solid #1e3a5f', background: '#eff6ff', color: '#1e3a5f', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
          {busy ? 'Opening…' : '📄 View the PO'}
        </button>
        <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 10, textAlign: 'center' }}>Something wrong on it? Ask the office to change it.</div>
      </div>
    </div>
  );
}

export default function ShopPos() {
  const navigate = useNavigate();
  const { worker } = useAuth();
  const [params, setParams] = useSearchParams();
  const [pos, setPos] = useState(null);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const [notFound, setNotFound] = useState('');
  const canWrite = !!(worker && (worker.canCreatePo || worker.role === 'ShopWorker'));

  useEffect(() => {
    apiFetch('/purchase-orders').then(d => setPos(Array.isArray(d) ? d : [])).catch(e => { setErr(e?.error || 'Could not load the purchase orders.'); setPos([]); });
  }, []);

  // ?po=20229 (or an id) opens that PO.
  useEffect(() => {
    const key = params.get('po') || params.get('open');
    if (!key || !pos) return;
    const hit = findPo(pos, key);
    if (hit) { setOpen(hit); setNotFound(''); } else { setNotFound(key); setQ(String(key).replace(/^(po\s*)?#?\s*/i, '')); }
  }, [params, pos]);

  function closeSheet() {
    setOpen(null);
    if (params.get('po') || params.get('open')) setParams({}, { replace: true });
  }

  const found = useMemo(() => {
    const list = (pos || []).slice().sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
    const s = q.trim().toLowerCase().replace(/^(po\s*)?#\s*/, '');
    if (!s) return { list: list.slice(0, 40), total: list.length };
    const words = s.split(/\s+/).filter(Boolean);
    const hits = list.filter(p => {
      const hay = [p.po_number, p.vendor, p.description, p.notes, p.project_number, p.project_name, who(p), p.status, p.equipment_asset, p.equipment_name].join(' ').toLowerCase();
      return words.every(w => hay.includes(w));
    });
    return { list: hits.slice(0, 100), total: hits.length };
  }, [pos, q]);
  const shown = found.list;

  return (
    <div style={{ minHeight: '100vh', background: '#f6f5f2', paddingBottom: 30 }}>
      <div style={{ background: '#1c1917', color: '#fff', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, position: 'sticky', top: 0, zIndex: 10 }}>
        <button onClick={() => navigate('/dashboard')} style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#fff', borderRadius: 10, padding: '8px 12px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>‹ Home</button>
        <div style={{ fontSize: 18, fontWeight: 800 }}>Purchase Orders</div>
      </div>

      <div style={{ padding: 16, maxWidth: 640, margin: '0 auto', boxSizing: 'border-box' }}>
        {canWrite && (
          <button onClick={() => navigate('/create-po')}
            style={{ width: '100%', padding: '18px 16px', borderRadius: 14, border: 'none', background: '#e87722', color: '#fff', fontSize: 17, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, boxShadow: '0 2px 8px rgba(232,119,34,0.3)' }}>
            <span style={{ fontSize: 24 }}>✍️</span> Write a New Purchase Order
          </button>
        )}

        <div style={{ marginTop: canWrite ? 22 : 0, fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>🔍 Search existing POs</div>
        <input value={q} onChange={e => { setQ(e.target.value); setNotFound(''); }} placeholder="PO number, vendor, job, what it's for…" inputMode="search"
          style={{ marginTop: 8, width: '100%', boxSizing: 'border-box', padding: '14px 14px', borderRadius: 12, border: '1.5px solid #d1d5db', fontSize: 16, background: '#fff' }} />
        {notFound && <div style={{ marginTop: 8, fontSize: 13, color: '#b45309', fontWeight: 700 }}>No PO #{String(notFound).replace(/^(po\s*)?#?\s*/i, '')} found — check the number.</div>}
        {err && <div style={{ marginTop: 8, fontSize: 13, color: '#b91c1c', fontWeight: 700 }}>{err}</div>}
        <div style={{ marginTop: 6, fontSize: 12, color: '#9ca3af' }}>
          {pos == null ? 'Loading…' : q.trim() ? `${found.total} match${found.total === 1 ? '' : 'es'}${found.total > shown.length ? ` — the newest ${shown.length} shown, type more to narrow it` : ''}` : 'Newest first'}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {shown.map(po => (
            <button key={po.id} onClick={() => setOpen(po)}
              style={{ textAlign: 'left', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '12px 14px', cursor: 'pointer', font: 'inherit' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16, fontWeight: 900, color: '#111827' }}>#{po.po_number}</span>
                <StatusChip status={po.status} />
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>{day(po.created_at)}</span>
              </div>
              <div style={{ fontSize: 14, color: '#374151', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{po.vendor || 'No vendor'}{po.description ? ' · ' + po.description : ''}</div>
              {jobOf(po) && <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📁 {jobOf(po)}</div>}
            </button>
          ))}
          {pos && !shown.length && !err && <div style={{ fontSize: 14, color: '#9ca3af', padding: '16px 0', textAlign: 'center' }}>No purchase orders match.</div>}
        </div>
      </div>
      {open && <PoSheet po={open} onClose={closeSheet} />}
    </div>
  );
}
